/**
 * Draft translation (Alt+R in the composer) — the CRM chat window's
 * translateChatText: MyMemory, source guessed from the script, results cached.
 */

const CACHE_MAX = 300;
const cache = new Map<string, string>();

export function detectLanguage(text: string): string {
  const raw = String(text || '');
  if (/[一-鿿]/.test(raw)) return 'zh-CN';
  if (/[぀-ヿ]/.test(raw)) return 'ja';
  if (/[가-힯]/.test(raw)) return 'ko';
  if (/[؀-ۿ]/.test(raw)) return 'ar';
  if (/[֐-׿]/.test(raw)) return 'he';
  if (/[฀-๿]/.test(raw)) return 'th';
  if (/[а-яёіїєґ]/i.test(raw)) return 'ru';
  if (/[áéíóúñ¿¡]/i.test(raw)) return 'es';
  if (/[àâçéèêëîïôùûüÿœ]/i.test(raw)) return 'fr';
  if (/[äöüß]/i.test(raw)) return 'de';
  if (/[àèéìíîòóùú]/i.test(raw)) return 'it';
  return 'en';
}

export class TranslateQuotaError extends Error {}

export async function translateText(text: string, target: string): Promise<string> {
  const raw = String(text || '').trim();
  if (!raw) return '';
  const source = detectLanguage(raw);
  if (source === target) return raw;
  const key = `${source}|${target}::${raw}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const res = await fetch(
    `https://api.mymemory.translated.net/get?q=${encodeURIComponent(raw.slice(0, 500))}&langpair=${encodeURIComponent(`${source}|${target}`)}`,
  );
  if (res.status === 429) throw new TranslateQuotaError('Daily translation limit reached');
  if (!res.ok) throw new Error(`Translate HTTP ${res.status}`);
  const data = (await res.json().catch(() => ({}))) as {
    responseStatus?: number | string;
    responseDetails?: string;
    responseData?: { translatedText?: string };
    matches?: { translation?: string }[];
  };
  const out = String(data.responseData?.translatedText || data.matches?.find((m) => m?.translation)?.translation || '').trim();
  // MyMemory answers an exhausted quota with 429 and a warning in place of the text.
  if (Number(data.responseStatus) === 429 || /MYMEMORY WARNING/i.test(out)) throw new TranslateQuotaError('Daily translation limit reached');
  if (data.responseStatus && Number(data.responseStatus) >= 400) throw new Error(data.responseDetails || 'Translation failed');
  if (!out) throw new Error('Translation is empty');
  if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value!);
  cache.set(key, out);
  return out;
}
